/**
 * 接口客户端（触屏机版）。
 *
 * 和管理后台的两点不同，都是因为「顾客在面前等着」这个场景：
 *   1. **没有登录态**：触屏机是匿名设备，凭据只有自己生成的顾客标识，不碰令牌；
 *   2. **超时必须短**：顾客不会等 30 秒。默认 10 秒拿不到回应就当成网络故障，
 *      界面立刻提示「请找店员」，而不是让顾客对着转圈发呆。
 *
 * 错误翻译统一走 `toApiError`：顾客看不懂 HTTP 状态码，
 * 必须翻译成「这道菜刚卖完了」这种他能据以行动的话。
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 网络层失败（压根没连上后端） */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /** 需要顾客重新确认菜单（售罄/下架/改价） */
  get needsMenuRefresh(): boolean {
    return this.code === 'SOLD_OUT' || this.code === 'PRODUCT_UNAVAILABLE' || this.code === 'PRICE_CHANGED';
  }
}

/** 请求默认超时（毫秒）。触屏机不吃「转圈三十秒」这一套。 */
export const DEFAULT_TIMEOUT_MS = 10_000;

function defaultMessageForStatus(status: number): string {
  if (status === 0) return '连不上点餐机，请叫服务员';
  if (status === 400) return '这一步没成功，请重新试一次';
  if (status === 404) return '这一单找不到了，请叫服务员';
  if (status === 409) return '订单状态变了，请叫服务员';
  if (status === 423) return '操作太频繁，请稍等一下';
  if (status === 501) return '这个支付方式还没开通';
  if (status >= 500) return '点餐机出了点问题，请叫服务员';
  return `操作没成功（${status}）`;
}

/**
 * 把 HTTP 响应翻译成 ApiError（纯函数，便于单元测试）。
 */
export function toApiError(status: number, body: unknown): ApiError {
  const payload = (body ?? {}) as Record<string, unknown>;
  const message =
    typeof payload.message === 'string' && payload.message.trim() !== ''
      ? payload.message
      : defaultMessageForStatus(status);
  const code = typeof payload.code === 'string' ? payload.code : `HTTP_${status}`;
  return new ApiError(code, message, status, payload.details);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** 覆盖默认超时（轮询支付状态这种要短一些） */
  timeoutMs?: number;
  signal?: AbortSignal;
}

export interface ApiClient {
  request<T>(path: string, options?: RequestOptions): Promise<T>;
}

export function createApiClient(apiBase: string): ApiClient {
  return {
    async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
      const { method = 'GET', body, timeoutMs = DEFAULT_TIMEOUT_MS, signal } = options;

      // 超时用 AbortController 实现：Node 18+/现代浏览器都有，
      // 不需要引第三方库，也不会在断网时永远挂着不返回。
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const onExternalAbort = () => controller.abort();
      signal?.addEventListener('abort', onExternalAbort);

      let response: Response;
      try {
        response = await fetch(`${apiBase}${path}`, {
          method,
          headers: body === undefined ? {} : { 'content-type': 'application/json' },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
          signal: controller.signal,
        });
      } catch (error) {
        const aborted = (error as Error)?.name === 'AbortError';
        throw new ApiError(
          aborted ? 'TIMEOUT' : 'NETWORK_ERROR',
          aborted ? '点餐机没有回应，请叫服务员' : '连不上点餐机，请叫服务员',
          0,
        );
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onExternalAbort);
      }

      const text = await response.text();
      let parsed: unknown = null;
      if (text) {
        try {
          parsed = JSON.parse(text);
        } catch {
          if (!response.ok) throw toApiError(response.status, null);
          throw new ApiError('BAD_RESPONSE', '点餐机返回了看不懂的内容，请叫服务员', response.status);
        }
      }

      if (!response.ok) throw toApiError(response.status, parsed);

      const payload = parsed as { data?: T } | null;
      if (payload && typeof payload === 'object' && 'data' in payload) {
        return payload.data as T;
      }
      return payload as T;
    },
  };
}
